import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  TestTube,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import apiService from '../../services/api';
import { AccountMatchingRule, PaginatedResponse } from '../../types/api';

interface RuleListProps {
  onCreateRule: () => void;
  onEditRule: (rule: AccountMatchingRule) => void;
  onTestRule: (rule: AccountMatchingRule) => void;
}

export const RuleList: React.FC<RuleListProps> = ({
  onCreateRule,
  onEditRule,
  onTestRule
}) => {
  const [rules, setRules] = useState<AccountMatchingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0
  });

  const loadRules = async (page = 1) => {
    setLoading(true);
    try {
      const response: PaginatedResponse<AccountMatchingRule> = await apiService.getAccountMatchingRules({
        page,
        per_page: 25
      });
      setRules(response.data);
      setPagination(response.meta);
    } catch (error) {
      console.error('Failed to load rules:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleDelete = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      await apiService.deleteAccountMatchingRule(ruleId);
      loadRules(pagination.current_page);
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  };

  const handleToggleActive = async (rule: AccountMatchingRule) => {
    try {
      await apiService.updateAccountMatchingRule(rule.id, {
        is_active: !rule.is_active
      });
      loadRules(pagination.current_page);
    } catch (error) {
      console.error('Failed to toggle rule:', error);
    }
  };

  const getPatternTypeDisplay = (type: string) => {
    switch (type) {
      case 'exact': return 'Exact Match';
      case 'contains': return 'Contains';
      case 'starts_with': return 'Starts With';
      case 'ends_with': return 'Ends With';
      case 'regex': return 'Regex';
      default: return type;
    }
  };

  const getTradeTypeDisplay = (type: string) => {
    switch (type) {
      case 'buy': return 'Buy Only';
      case 'sell': return 'Sell Only';
      case 'both': return 'Buy & Sell';
      default: return type;
    }
  };

  const filteredRules = rules.filter(rule =>
    rule.pattern_value.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rule.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="card">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Account Matching Rules</h2>
          <p className="text-gray-600 mt-1">
            Manage rules for automatically matching account strings to commission rates
          </p>
        </div>

        <button
          onClick={onCreateRule}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>New Rule</span>
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search rules by pattern or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Rules Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pattern
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commission Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trade Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-800 text-sm font-medium">
                        {rule.priority}
                      </span>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="text-sm font-medium text-gray-900">
                        {rule.pattern_value}
                      </div>
                      <div className="text-sm text-gray-500">
                        {getPatternTypeDisplay(rule.pattern_type)}
                        {rule.security_pattern && (
                          <span> • Security: {rule.security_pattern}</span>
                        )}
                      </div>
                      {rule.description && (
                        <div className="text-xs text-gray-400 mt-1">
                          {rule.description}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {parseFloat(rule.commission_rate).toFixed(4)}%
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="badge badge-info">
                      {getTradeTypeDisplay(rule.trade_type)}
                    </span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(rule)}
                      className={`
                        flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
                        ${rule.is_active
                          ? 'bg-success-100 text-success-800 hover:bg-success-200'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }
                      `}
                    >
                      {rule.is_active ? (
                        <>
                          <Check className="w-3 h-3" />
                          <span>Active</span>
                        </>
                      ) : (
                        <>
                          <X className="w-3 h-3" />
                          <span>Inactive</span>
                        </>
                      )}
                    </button>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onTestRule(rule)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Test rule"
                      >
                        <TestTube className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => onEditRule(rule)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Edit rule"
                      >
                        <Edit className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(rule.id)}
                        className="text-danger-600 hover:text-danger-900"
                        title="Delete rule"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRules.length === 0 && (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No matching rules found' : 'No rules configured'}
            </h3>
            <p className="text-gray-500">
              {searchTerm
                ? 'Try adjusting your search criteria'
                : 'Create your first account matching rule to get started'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={onCreateRule}
                className="btn btn-primary mt-4"
              >
                Create First Rule
              </button>
            )}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">How Account Matching Works</h4>
            <div className="text-sm text-blue-800 mt-1 space-y-1">
              <p>• Rules are evaluated in priority order (lower numbers = higher priority)</p>
              <p>• The first matching rule determines the commission rate</p>
              <p>• Use "exact" patterns for specific cases like "INTERVAL" vs "INTERVAL BIDS"</p>
              <p>• Inactive rules are skipped during matching</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};